// src/components/RightRail.jsx
// The icon strip on the right (3.48.7, desktop): Your account (My profile,
// Security, Preferences, Sessions, My notes, View as) and Help (How do I…?,
// Keyboard shortcuts, Support, Feedback, What's new), which used to sit in
// the menu under your name. Like the left strip (SideRail): icons always
// showing, so people know they're there; resting the mouse on it (or moving
// into it with the keyboard) slides it open with each item's name, and it
// slides closed when the mouse leaves. A click opens the item. Phones keep
// these in the menu under your name instead.
import { useEffect, useRef, useState } from 'react';
import Ambient from './Ambient';
import Emo from './Emo';
import { RAIL_WIDTH } from './SideRail';

export const RIGHT_MENU_OPEN = 230;   // px wide when slid open (3.56.2: App keeps the bell clear of it)

// groups: [{ title, items: [{ id, icon, label, hint, onClick, count, extra, tone }] }]
// 3.53.0: forceOpen keeps it slid open (the guided tour points at it)
export default function RightRail({ groups, top = 0, forceOpen = false }) {
  const [hoverOpen, setOpen] = useState(false);
  const open = hoverOpen || forceOpen;
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const later = (v, ms) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(v), ms); };
  const shown = groups.filter(g => g.items.length);
  if (!shown.length) return null;
  return (
    <nav aria-label="Your account and help" data-tour="right-rail" className="cp-ambient-host cp-night" style={{ ...S.rail, top, zIndex: open ? 101 : 95, width: open ? RIGHT_MENU_OPEN : RAIL_WIDTH, boxShadow: open ? '-6px 0 24px rgb(var(--c-000000-rgb) / 0.12)' : 'none' }}
      onMouseEnter={() => later(true, 120)} onMouseLeave={() => later(false, 180)}
      onFocus={() => later(true, 0)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) later(false, 0); }}>
      <Ambient sheets={3} />
      <div style={S.hint} className="rail-muted" aria-hidden="true">
        <span style={S.chev} className="rail-chev">{open ? '»' : '«'}</span>
        <span style={{ ...S.label, opacity: open ? 1 : 0 }}>You</span>
      </div>
      {shown.map((g, gi) => (
        <div key={g.title} role="group" aria-label={g.title} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ ...S.group, ...(gi ? S.groupNext : {}) }} className="rail-muted" aria-hidden="true">
            <span style={{ ...S.label, opacity: open ? 1 : 0 }}>{g.title}</span>
          </div>
          {g.items.map(it => (
            <button key={it.id} type="button" title={it.hint} data-tour={`me-${it.id}`} className={`rail-item rail-item-right${it.tone === 'warn' ? ' warn' : ''}`}
              onClick={e => {
                setOpen(false);
                it.onClick();
                if (e.detail > 0) e.currentTarget.blur();   // a mouse click doesn't leave it focused
              }}
              style={{ ...S.item, ...(it.tone === 'warn' ? { color: 'var(--t-e65100)' } : {}) }}>
              <span style={S.icon} aria-hidden="true"><Emo e={it.icon} />{it.count > 0 && <span style={S.dot} />}</span>
              <span style={{ ...S.label, opacity: open ? 1 : 0 }}>
                {it.label}
                {it.count > 0 && <span style={S.count} aria-label={`${it.count} new`}>{it.count}</span>}
                {it.extra && <span style={S.extra} className="rail-muted">{it.extra}</span>}
              </span>
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

const S = {
  rail: { position: 'fixed', right: 0, bottom: 0, zIndex: 95, borderLeft: '1px solid var(--b-c8e6c9)', overflowX: 'hidden', overflowY: 'auto',
    paddingTop: 8, transition: 'width .2s cubic-bezier(.2,.8,.2,1), box-shadow .2s', display: 'flex', flexDirection: 'column', gap: 2 },
  hint: { display: 'flex', alignItems: 'center', gap: 12, padding: '4px 18px 4px', color: 'var(--t-90a4ae)', fontSize: 12, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  chev: { width: 24, textAlign: 'center', fontSize: 16, color: 'var(--t-00897b)' },
  item: { display: 'flex', alignItems: 'center', gap: 10, margin: '0 8px', padding: '7px 8px', borderRadius: 10, border: 'none', background: 'none', cursor: 'pointer',
    color: 'var(--t-37474f)', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'left', transition: 'background .15s' },
  icon: { position: 'relative', width: 28, textAlign: 'center', fontSize: 19, flexShrink: 0 },
  label: { transition: 'opacity .15s' },
  group: { padding: '2px 18px 0', minHeight: 14, fontSize: 10, fontWeight: 800, letterSpacing: '0.7px', textTransform: 'uppercase', color: 'var(--t-90a4ae)', whiteSpace: 'nowrap' },
  groupNext: { marginTop: 6 },
  dot: { position: 'absolute', top: -2, right: -2, width: 9, height: 9, borderRadius: '50%', background: 'var(--c-bf360c)', border: '2px solid var(--c-ffffff)' },
  count: { display: 'inline-block', minWidth: 18, padding: '1px 6px', marginLeft: 8, borderRadius: 10, background: 'var(--c-bf360c)', color: 'var(--t-ffffff)', fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center' },
  extra: { fontSize: 11, color: 'var(--t-90a4ae)', marginLeft: 6, fontWeight: 500 },
};
